
import { ChartStylesModel } from '../../Charts/models/chart-styles.model';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { SendSharedReportApiModel, SharedReportSendTypeEnum } from '../../../api/reports/model/send-shared-report-api.model';
import { CreateSharedReportApiModel, SharedReportApiModel, UpdateSharedReportApiModel } from '../../../api/reports/model/shared-report-api.model';
import { SharedReportBlockApiModel } from '../../../api/reports/model/shared-report-block-api.model';
import { basicClone, Clonable } from '../../Basic/decorators/clone.decorator';
import { LabelDisplayOptionsEnum } from '../../Misc/enums/label-display-options.enum';
import { DimensionsModel } from '../../Misc/models/dimensions.model';
import { BlockTypeModel } from '../../PageBuilder/models/block-type.model';
import { BlockModel } from '../../PageBuilder/models/block.model';
import { GenericStyleModel } from '../../Styles/models/generic-style.model';
import { ReportBlockTypeEnum, reportBlockTypeEnumFromApi, reportBlockTypeEnumToApi, reportBlockTypeEnumToDefaultChartSpecificType } from '../enums/report-block-type.enum';
import { ChartDisplayElementModel } from './chart-display-element.model';
import { ReportSeriesModel } from './report-series.model';
import { reportsBlockTypes } from './reports-block-types.model';
import { TopMentionsMetadataShareableModel } from '../../Charts/models/top-mentions-metadata-shareable.model';
import { TopMentionsMetadataModel } from '../../Charts/models/top-mentions-metadata.model';
import { SharedReportDataVersioner } from '../../../Common/PageBuilder/helpers/SharedReportDataVersioner/shared-report-data-versioner.helper';
import { ReportBlockTypeApiEnum } from '../../../api/reports/enum/report-block-type-api.enum';
import { MentionCustomizationModel } from '../../../Monitor/Shared/models/mention-customization.model';
import { MentionMetadataTypesEnum } from '../../../Monitor/Shared/enums/mention-metadata-types.enum';
import { ChartAnnotations } from './chart-annotations.model';

export class SharedReportModel {
    @Clonable()
    id: number;
    @Clonable()
    name: string;
    @Clonable()
    reportId: number;
    @Clonable()
    guid: string;
    @Clonable()
    coverPageImageUrl: string;
    @Clonable()
    charts: BlockModel[] = [];
    @Clonable()
    mailingListIds: number[] = [];
    @Clonable()
    recipientList: string[] = [];
    @Clonable()
    inviteMessage: string;
    @Clonable()
    fromName: string;
    @Clonable()
    replyTo: string;
    @Clonable()
    expiryDate: Date;

    @Clonable()
    createdByUserId: number;
    @Clonable()
    createdDate: Date;
    @Clonable()
    modifiedByUserId: number;
    @Clonable()
    modifiedDate?: Date;

    @Clonable()
    accountId: number;
    @Clonable()
    userId: number;

    @Clonable()
    coverTitle: string;

    dateFormat: string;

    /**
     * Class constructor
     */
    constructor(apiModel?: SharedReportApiModel) {
        if (apiModel) {
            this.id = apiModel.id;
            this.name = apiModel.name;
            this.reportId = apiModel.sourceReportId;
            this.guid = apiModel.guid;
            this.mailingListIds = apiModel.mailingListIds.slice();
            this.recipientList = apiModel.recipients.slice();
            this.inviteMessage = apiModel.emailInviteMessage;
            this.fromName = apiModel.fromName;
            this.replyTo = apiModel.replyTo;
            this.expiryDate = new Date(apiModel.expiryDate);
            this.createdByUserId = apiModel.userId;
            this.createdDate = new Date(apiModel.createdDate);
            this.modifiedByUserId = apiModel.lastModifiedByUserId;
            this.modifiedDate = new Date(apiModel.lastModifiedDate);
            this.accountId = apiModel.accountId;
            this.userId = apiModel.userId;

            const data = JSON.parse(apiModel.data);
            this.charts = this.mapBlocksFromApi(data.blocks);
            this.coverPageImageUrl = data.imageUrl;
            this.coverTitle = data.coverTitle;
            if (!this.coverTitle) {
                this.coverTitle = this.name;
            }
            this.dateFormat = data.dateFormat;
        }
    }

    /**
     * Converts this front end model to the back end version
     */
    public toApiCreateModel(): CreateSharedReportApiModel {
        return {
            name: this.name,
            sourceReportId: this.reportId,
            recipients: this.recipientList.slice(),
            mailingListIds: this.mailingListIds.slice(),
            emailInviteMessage: this.inviteMessage,
            fromName: this.fromName,
            replyTo: this.replyTo,
            accountId: this.accountId,
            data: JSON.stringify({
                blocks: this.mapBlocksToApi(this.charts),
                imageUrl: this.coverPageImageUrl,
                coverTitle: this.coverTitle,
                dateFormat: this.dateFormat
            })
        };
    }

    /**
     * Converts this front end model to the back end version
     */
    public toApiUpdateModel(): UpdateSharedReportApiModel {
        return {
            id: this.id,
            name: this.name,
            sourceReportId: this.reportId,
            recipients: this.recipientList.slice(),
            mailingListIds: this.mailingListIds.slice(),
            emailInviteMessage: this.inviteMessage,
            fromName: this.fromName,
            replyTo: this.replyTo,
            accountId: this.accountId,
            data: JSON.stringify({
                blocks: this.mapBlocksToApi(this.charts),
                imageUrl: this.coverPageImageUrl,
                coverTitle: this.coverTitle,
                dateFormat: this.dateFormat
            })
        };
    }

    /**
     * Converts ReportBlockApiModel[] to BlockModel[]
     */
    protected mapBlocksFromApi(apiBlocks: SharedReportBlockApiModel[] = []): BlockModel[] {
        const blocks: BlockModel[] = [];

        const blockTypeMap: Map<ReportBlockTypeEnum, BlockTypeModel> = this.makeBlockTypeMap(reportsBlockTypes);

        for (const apiBlock of apiBlocks) {
            const blockType = blockTypeMap.get(reportBlockTypeEnumFromApi.get(apiBlock.type));

            if (!blockType) {

                return;
            }

            const block: BlockModel = new BlockModel(
                apiBlock.id,
                blockType,
                new (blockType.displayElementType)()
            );

            if (reportBlockTypeEnumFromApi.has(apiBlock.type)) {
                const chartDisplayElement = block.displayElement as ChartDisplayElementModel;
                chartDisplayElement.series = apiBlock.series.map(
                    reportApiSeriesModel => new ReportSeriesModel(
                        reportApiSeriesModel,
                        reportBlockTypeEnumFromApi.get(apiBlock.type)
                    )
                );
                chartDisplayElement.originalReportBlockId = apiBlock.originalReportBlockId;
                chartDisplayElement.note = apiBlock.note;

                const data = new GenericStyleModel(apiBlock.data);
                chartDisplayElement.sentiments = data.sentiments;
                chartDisplayElement.labelDisplayOptions = data.labelDisplayOptions;
                chartDisplayElement.metric = data.metric;
                chartDisplayElement.metrics = data.metrics;
                chartDisplayElement.chartSpecificType = data.chartSpecificType
                    || reportBlockTypeEnumToDefaultChartSpecificType.get(block.type.key as ReportBlockTypeEnum);
                chartDisplayElement.chartHeaderSettings = data.chartHeaderSettings;
                chartDisplayElement.displayAnnotationInExport = data.displayAnnotationInExport;
                chartDisplayElement.chartAnnotations = new ChartAnnotations(data.chartAnnotations?.indicators, data.chartAnnotations?.markerState);

                // if the data is older it won't contain options for sentiment or label display
                // provide the defaults here:
                if (!data.labelDisplayOptions) {
                    chartDisplayElement.labelDisplayOptions = [
                        LabelDisplayOptionsEnum.percent
                    ];
                }

                if (!data.sentiments) {
                    chartDisplayElement.sentiments = [
                        SearchCriteriaQuerySentimentsEnum.positive,
                        SearchCriteriaQuerySentimentsEnum.neutral,
                        SearchCriteriaQuerySentimentsEnum.negative,
                        SearchCriteriaQuerySentimentsEnum.notAvailable
                    ];
                }

                // label display options were briefly stored directly on the 'checkboxOptions' property instead
                // of on 'labelDisplayOptions' - this is a compatibility layer
                if (data.checkboxOptions && data.checkboxOptions.includes(LabelDisplayOptionsEnum.percent)) {
                    chartDisplayElement.labelDisplayOptions.push(LabelDisplayOptionsEnum.percent);
                    data.checkboxOptions.splice(data.checkboxOptions.indexOf(LabelDisplayOptionsEnum.percent), 1);
                }

                if (data.checkboxOptions && data.checkboxOptions.includes(LabelDisplayOptionsEnum.count)) {
                    chartDisplayElement.labelDisplayOptions.push(LabelDisplayOptionsEnum.count);
                    data.checkboxOptions.splice(data.checkboxOptions.indexOf(LabelDisplayOptionsEnum.count), 1);
                }

                if (apiBlock.type === ReportBlockTypeApiEnum.TOP_MENTIONS) {
                    const topMentionsMetadataModel = new MentionCustomizationModel();
                    const topMentionsMetadata = data.topMentionsMetadata;

                    const metadataTypes: MentionMetadataTypesEnum[] = [];
                    Object.keys(topMentionsMetadata.metadata).forEach(key => {
                        if (topMentionsMetadata.metadata[key]) {
                            metadataTypes.push(key as MentionMetadataTypesEnum);
                        }
                    });

                    topMentionsMetadataModel.metadataTypes = metadataTypes;
                    topMentionsMetadataModel.mentionContent = topMentionsMetadata.mentionContent;
                    topMentionsMetadataModel.showInsights = topMentionsMetadata.showInsights;
                    topMentionsMetadataModel.insightsNameDisplay = topMentionsMetadata.insightsNameDisplay;
                    topMentionsMetadataModel.customInsightsName = topMentionsMetadata.customInsightsName;
                    topMentionsMetadataModel.timezone = topMentionsMetadata.timezone;
                    topMentionsMetadataModel.currencyRate = topMentionsMetadata.currencyRate;
                    topMentionsMetadataModel.preferredCurrency = topMentionsMetadata.preferredCurrency;
                    topMentionsMetadataModel.highlightKeywords = topMentionsMetadata.highlightKeywords;

                    chartDisplayElement.topMentionsMetadata = topMentionsMetadataModel;
                }

                if (apiBlock.type === ReportBlockTypeApiEnum.REFERRAL_TREND
                    || apiBlock.type === ReportBlockTypeApiEnum.COVERAGE_IMPACT_TREND) {
                    chartDisplayElement.referralTypes = data.referralTypes;
                }

                delete data.sentiments;
                delete data.labelDisplayOptions;

                chartDisplayElement.styles = new ChartStylesModel(data);
                chartDisplayElement.title = apiBlock.title;
                chartDisplayElement.size = new DimensionsModel(block.rows, block.cols);

                block.displayElement = chartDisplayElement;

                SharedReportDataVersioner.transformDataToNewestVersion(block, data);
            }

            blocks.push(block);
        }

        return blocks;
    }

    /**
     * Converts BlockModel[] to ReportBlockApiModel[]
     */
    protected mapBlocksToApi(blocks: BlockModel[]): SharedReportBlockApiModel[] {
        const apiBlocks: SharedReportBlockApiModel[] = [];

        for (const block of blocks) {
            if (!block.type || !block.type.key) {
                return;
            }

            if (reportBlockTypeEnumToApi.has(block.type.key as ReportBlockTypeEnum)) {
                const chartDisplayElement = block.displayElement as ChartDisplayElementModel;

                const data = chartDisplayElement.styles;
                data.sentiments = chartDisplayElement.sentiments;
                data.labelDisplayOptions = chartDisplayElement.labelDisplayOptions;
                data.displayAnnotationInExport = chartDisplayElement.displayAnnotationInExport;
                data.chartAnnotations = chartDisplayElement.chartAnnotations?.clone();

                switch (block.type.key) {
                    case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                    case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                        data.chartOptions.prevents = chartDisplayElement.prevents;
                        break;
                    case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                        data.chartOptions.geographyScale = chartDisplayElement.styles.chartGeographyType;
                        data.chartOptions.regionIsoCode = chartDisplayElement.styles.regionIsoCode;
                        break;
                    case ReportBlockTypeEnum.TOP_MENTIONS:
                        const mentionCustomization = chartDisplayElement.topMentionsMetadata;
                        const metadata = new TopMentionsMetadataModel();
                        chartDisplayElement.topMentionsMetadata?.metadataTypes?.forEach(type => metadata[type] = true);
                        data.topMentionsMetadata = new TopMentionsMetadataShareableModel(
                            mentionCustomization.mentionContent,
                            metadata,
                            mentionCustomization.showInsights,
                            mentionCustomization.insightsNameDisplay,
                            mentionCustomization.customInsightsName,
                            mentionCustomization.timezone,
                            mentionCustomization.currencyRate,
                            mentionCustomization.preferredCurrency,
                            mentionCustomization.highlightKeywords
                        );
                        break;
                    case ReportBlockTypeEnum.REFERRAL_TREND:
                    case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                        data.referralTypes = chartDisplayElement.referralTypes;
                        break;
                }

                apiBlocks.push({
                    id: block.id,
                    type: reportBlockTypeEnumToApi.get(block.type.key as ReportBlockTypeEnum),
                    title: chartDisplayElement.title,
                    data: JSON.stringify(data.toApi()),
                    series: (chartDisplayElement.series || []).map(series => series.toSharedReportSeriesApiModel()).filter(x => !!x),
                    note: chartDisplayElement.note,
                    originalReportBlockId: chartDisplayElement.originalReportBlockId
                } as SharedReportBlockApiModel);

                delete data.sentiments;
                delete data.labelDisplayOptions;
            }

            if (!block.id) {
                delete block.id;
            }
        }

        return apiBlocks;
    }

    /**
     * Deep compare against another shared report model
     */
    public equals(other: SharedReportModel): boolean {
        return true;
    }

    /**
     * Returns a clone of this SharedReportModel
     */
    public clone(): SharedReportModel {
        return basicClone(this);
    }

    public getSendModel(subject: string, sendTo: SharedReportSendTypeEnum = SharedReportSendTypeEnum.ALL): SendSharedReportApiModel {
        return {
            emailSubject: subject,
            inviteMessage: this.inviteMessage,
            mailingListIds: this.mailingListIds,
            recipientEmails: this.recipientList,
            sendType: sendTo
        };
    }

    /**
     * Converts the BlockTypeModel[] into a map for lookup efficiency
     */
    protected makeBlockTypeMap(blockTypes: BlockTypeModel[]): Map<ReportBlockTypeEnum, BlockTypeModel> {
        const map = new Map<ReportBlockTypeEnum, BlockTypeModel>();

        blockTypes.forEach(
            blockType => {
                map.set(blockType.key as ReportBlockTypeEnum, blockType);
            }
        );

        return map;
    }
}
